<template>
  <div class="carousel-keen-wrap relative">
    <div ref="sl" class="keen-slider">
      <slot></slot>
    </div>

    <!-- Desktop: scroll indicator -->
    <div
      v-if="progressbar"
      class="mt-20 hidden h-10 cursor-pointer overflow-hidden bg-[#E6E6E6] lg:block relative"
      ref="progressTrack"
      @mousedown="onProgressMouseDown"
    >
      <div
        class="h-full bg-green absolute top-0"
        :style="{width: thumbWidth + '%', left: thumbLeft + '%'}"
      ></div>
    </div>

    <!-- Mobile: arrow buttons centered  -->
    <div v-if="arrows" class="mt-20 flex items-center justify-center gap-15 lg:hidden">
      <button
        class="flex h-[30px] w-[30px] items-center justify-center rounded-full border-2 border-blue/50 text-blue/50 active:border-blue active:text-blue transition-all "
        @click="sl && sl.prev()"
        aria-label="Previous"
      >
        <slot name="arrow-prev">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="15" viewBox="0 0 14 15" fill="none" class="rotate-180">
            <path d="M13.5012 6.65691C13.8917 7.04743 13.8917 7.6806 13.5012 8.07112L7.13726 14.4351C6.74674 14.8256 6.11357 14.8256 5.72305 14.4351C5.33253 14.0446 5.33253 13.4114 5.72305 13.0209L11.3799 7.36401L5.72305 1.70716C5.33253 1.31664 5.33253 0.683471 5.72305 0.292946C6.11358 -0.097578 6.74674 -0.0975779 7.13726 0.292946L13.5012 6.65691ZM0 7.36401L8.74228e-08 6.36401L12.7941 6.36401L12.7941 7.36401L12.7941 8.36401L-8.74228e-08 8.36401L0 7.36401Z" fill="currentColor"/>
          </svg>
        </slot>
      </button>
      <button
        class="flex h-[30px] w-[30px] items-center justify-center rounded-full border-2 border-blue/50 text-blue/50 active:border-blue active:text-blue transition-all "
        @click="sl && sl.next()"
        aria-label="Next"
      >
        <slot name="arrow-next">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="15" viewBox="0 0 14 15" fill="none">
            <path d="M13.5012 6.65691C13.8917 7.04743 13.8917 7.6806 13.5012 8.07112L7.13726 14.4351C6.74674 14.8256 6.11357 14.8256 5.72305 14.4351C5.33253 14.0446 5.33253 13.4114 5.72305 13.0209L11.3799 7.36401L5.72305 1.70716C5.33253 1.31664 5.33253 0.683471 5.72305 0.292946C6.11358 -0.097578 6.74674 -0.0975779 7.13726 0.292946L13.5012 6.65691ZM0 7.36401L8.74228e-08 6.36401L12.7941 6.36401L12.7941 7.36401L12.7941 8.36401L-8.74228e-08 8.36401L0 7.36401Z" fill="currentColor"/>
          </svg>
        </slot>
      </button>
    </div>
  </div>
</template>

<script>
import KeenSlider from "keen-slider";
import "keen-slider/keen-slider.min.css";

export default {
  name: "CarouselKeen",
  props: {
    slides: {
      type: [Number, String],
      default: 2.2,
    },
    mobile_slides: {
      type: Number,
      default: 1.3,
    },
    tablet_slides: {
      type: Number,
      default: 2,
    },
    loop: {
      type: Boolean,
      default: false,
    },
    spacing: {
      type: Number,
      default: 20,
    },
    progressbar: {
      type: Boolean,
      default: true,
    },
    autoplay: {
      type: Number,
      default: 0,
    },
    arrows: {
      type: Boolean,
      default: true,
    },
    centered: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      current: 0,
      progress: 0,
      totalSlides: 0,
      sl: null,
      autoplayInterval: null,
    };
  },

  computed: {
    visibleSlides() {
      if (typeof window === 'undefined') return this.slides;
      var w = window.innerWidth;
      if (w <= 640) return this.mobile_slides;
      if (w <= 1024) return this.tablet_slides;
      return this.slides;
    },
    thumbWidth() {
      if (!this.totalSlides || this.totalSlides <= 1) return 100;
      return Math.min(30, Math.max(15, (this.visibleSlides / this.totalSlides) * 100)).toFixed(1);
    },
    thumbLeft() {
      if (!this.totalSlides || this.totalSlides <= 1) return 0;
      var tw = parseFloat(this.thumbWidth);
      return (this.progress * (100 - tw)).toFixed(1);
    },
  },

  methods: {
    initSlider() {
      this.$nextTick(() => {
        var slideEls = this.$refs.sl.querySelectorAll(".keen-slider__slide");
        this.totalSlides = slideEls.length;

        this.sl = new KeenSlider(this.$refs.sl, {
          loop: false,
          initial: 0,
          slideChanged: s => {
            this.current = s.track.details.rel;
            this.progress = s.track.details.progress;
          },
          detailsChanged: s => {
            this.current = s.track.details.rel;
            this.progress = s.track.details.progress;
          },
          breakpoints: {
            "(max-width: 1024px)": {
              loop: this.loop,
              slides: {
                perView: this.tablet_slides,
                spacing: this.spacing / 2,
              },
            },
            "(max-width: 640px)": {
              loop: this.loop,
              slides: {
                perView: this.mobile_slides,
                spacing: 10,
                origin: this.centered ? "center" : "auto",
              },
            },
          },
          slides: {
            perView: this.slides,
            spacing: this.spacing,
          },
        });

        if (this.autoplay > 0) {
          this.startAutoplay();
          this.$refs.sl.addEventListener("mouseenter", this.stopAutoplay);
          this.$refs.sl.addEventListener("mouseleave", this.startAutoplay);
        }
      });
    },

    startAutoplay() {
      this.stopAutoplay();
      this.autoplayInterval = setInterval(() => {
        if (this.sl) this.sl.next();
      }, this.autoplay);
    },

    stopAutoplay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
        this.autoplayInterval = null;
      }
    },

    onProgressMouseDown(e) {
      if (!this.sl || !this.$refs.progressTrack) return;
      e.preventDefault();
      this.scrubToPosition(e.clientX);

      var self = this;
      function onMouseMove(ev) {
        ev.preventDefault();
        self.scrubToPosition(ev.clientX);
      }
      function onMouseUp() {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      }
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },

    scrubToPosition(clientX) {
      var rect = this.$refs.progressTrack.getBoundingClientRect();
      var percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      var maxIdx = this.totalSlides - 1;
      var targetSlide = Math.round(percent * maxIdx);
      this.sl.moveToIdx(targetSlide, true, { duration: 300 });
    },
  },

  mounted() {
    this.initSlider();
  },

  beforeUnmount() {
    this.stopAutoplay();
    if (this.sl) {
      if (this.autoplay > 0) {
        this.$refs.sl.removeEventListener("mouseenter", this.stopAutoplay);
        this.$refs.sl.removeEventListener("mouseleave", this.startAutoplay);
      }
      this.sl.destroy();
    }
  },
};
</script>
